// Telemetry: fire-and-forget usage events (docs/SPEC_TELEMETRY.md). The game
// must stay fully playable with the API server down, unreachable, or
// ad-blocked — nothing here throws, retries, or blocks a gameplay path.
//
// Identity is one random UUID per browser tab (sessionStorage); the unique-
// visitor id is an IP hash computed server-side. No cookies, no fingerprint.
// Dev traffic is sent too — the server flags local IPs rather than the client
// suppressing itself, so the pipeline is testable end-to-end.

type Props = Record<string, string | number | boolean>;

const ENDPOINT = `${import.meta.env.VITE_API_BASE ?? ""}/api/t`;
const SESSION_KEY = "first-silicon:telemetry:session";
const FLUSH_MS = 20_000;
const FLUSH_AT = 20; // queued events

const startedAt = Date.now();
let queue: { name: string; at: number; props: Props }[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let turnsPlayed = 0;
let cachedId: string | null = null;
let began = false;
let ended = false;

function sessionId(): string {
  if (cachedId) return cachedId;
  let id: string;
  try {
    id = crypto.randomUUID();
  } catch {
    // non-secure context: good enough for a per-tab visit id
    id = `${Date.now().toString(16)}-xxxx-4xxx-8xxx-xxxxxxxxxxxx`.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
  }
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) id = stored;
    else sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    // storage unavailable: in-memory id still identifies this tab's visit
  }
  cachedId = id;
  return id;
}

function flush(useBeacon = false): void {
  if (queue.length === 0) return;
  const body = JSON.stringify({ sessionId: sessionId(), events: queue });
  queue = [];
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
    } else {
      void fetch(ENDPOINT, {
        method: "POST",
        keepalive: true,
        headers: { "content-type": "application/json" },
        body,
      }).catch(() => {});
    }
  } catch {
    // telemetry never breaks the game
  }
}

export function track(name: string, props: Props = {}): void {
  if (name === "turn_end") turnsPlayed += 1;
  queue.push({ name, at: Date.now(), props });
  if (queue.length >= FLUSH_AT) flush();
  else if (timer === null) {
    timer = setTimeout(() => {
      timer = null;
      flush();
    }, FLUSH_MS);
  }
}

/** Called once from the App mount effect. Fires session_start, starts the
 *  heartbeat, and arms the tab-close flush. The guard also absorbs
 *  StrictMode's dev double-mount. */
export function beginSession(props: Props, getTurn: () => number): void {
  if (began) return;
  began = true;
  track("session_start", props);
  flush(); // make the visit visible immediately instead of 20s later
  setInterval(() => {
    if (document.visibilityState === "visible") track("heartbeat", { turn: getTurn() });
  }, 60_000);
  window.addEventListener("pagehide", () => {
    if (!ended) {
      ended = true;
      track("session_end", { durationMs: Date.now() - startedAt, turnsPlayed });
    }
    flush(true);
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true); // beacon survives a backgrounded-tab kill
  });
}
